import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./auth/AuthContext";
import { LoadingScreen } from "./components/LoadingScreen";
import { Login } from "./routes/Login";
import { Feed } from "./routes/Feed";
import { PostDetail } from "./routes/PostDetail";
import { AdminPosts } from "./routes/admin/AdminPosts";
import { PostEditor } from "./routes/admin/PostEditor";
import { AdminFriends } from "./routes/admin/AdminFriends";

/** Gate authed routes. Auth is *also* enforced server-side on every API call. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  if (status === "loading") return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** The `/` route. Messy's home is the admin area — send him there unless he's
 *  explicitly previewing the public site (Go To Public). Friends see the feed. */
function FeedRoute() {
  const { user, previewPublic } = useAuth();
  if (user?.role === "admin" && !previewPublic) return <Navigate to="/admin" replace />;
  return <Feed />;
}

/** Gate admin routes. The API also enforces admin on every /api/admin call. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  if (status === "loading") return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const { status, user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          status === "loading" ? (
            <LoadingScreen />
          ) : user ? (
            <Navigate to="/" replace />
          ) : (
            <Login />
          )
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <FeedRoute />
          </RequireAuth>
        }
      />
      <Route
        path="/post/:id"
        element={
          <RequireAuth>
            <PostDetail />
          </RequireAuth>
        }
      />

      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminPosts />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/posts/new"
        element={
          <RequireAdmin>
            <PostEditor />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/posts/:id/edit"
        element={
          <RequireAdmin>
            <PostEditor />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/friends"
        element={
          <RequireAdmin>
            <AdminFriends />
          </RequireAdmin>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
